import { Chip } from '@mui/material';
import dayjs from 'dayjs';
import timezone from 'dayjs/plugin/timezone';
import utc from 'dayjs/plugin/utc';

import { CalendarSyncPair, SlotRebuildStatus } from 'services/selfSchedulingServices';

dayjs.extend(utc);
dayjs.extend(timezone);

/**
 * MM-1198: every date/time on the Sync Calendar and Sync Jobs pages is shown in US Central time (the offices'
 * time zone, as DrChrono and the backend use it), whoever is looking - without a time zone label.
 */
export const OFFICE_TIME_ZONE = 'America/Chicago';

/** MM-1198: a time stamp (epoch ms) in Central time. */
export const toOfficeTime = (value: number) => dayjs(value).tz(OFFICE_TIME_ZONE);

/** MM-1198: a time stamp (epoch ms) formatted in Central time. */
export const formatOfficeTime = (value: number, pattern: string) => toOfficeTime(value).format(pattern);

/** MM-1168: label and chip color of each rebuild status, as stored in the DB. */
const STATUS_CHIPS: Record<
  SlotRebuildStatus,
  { label: string; color: 'default' | 'info' | 'success' | 'warning' | 'error' }
> = {
  PENDING: { label: 'Pending', color: 'default' },
  IN_PROGRESS: { label: 'In progress', color: 'info' },
  COMPLETED: { label: 'Completed', color: 'success' },
  COMPLETED_WITH_ERRORS: { label: 'Completed with errors', color: 'warning' },
  FAILED: { label: 'Failed', color: 'error' },
  RETRY_LIMIT_REACHED: { label: 'Retry limit reached', color: 'error' },
};

/** MM-1168: error text color used in the Sync Calendar tables. */
export const ERROR_TEXT_COLOR = '#B42318';

/** MM-1168: a tracking date as shown in the tables (MM-1198: in Central time), or '-' when not set. */
export const formatDate = (value: number | null) => (value ? formatOfficeTime(value, 'MM/DD/YYYY hh:mm A') : '-');

/**
 * MM-1169: the automatic retry state of a pair - the next retry while one is due, else how many
 * retries the latest rebuild used; null when it used none.
 */
export const retryText = (pair: CalendarSyncPair) => {
  if (pair.nextRetryDate) {
    return `Retry ${pair.retryCount + 1}/${pair.retryLimit} at ${formatOfficeTime(pair.nextRetryDate, 'hh:mm A')}`;
  }
  return pair.retryCount ? `Retries used ${pair.retryCount}/${pair.retryLimit}` : null;
};

/** MM-1168: the rebuild status as a colored chip; empty (never rebuilt) is shown as Pending. */
export const StatusChip = ({ status }: { status: SlotRebuildStatus | null }) => {
  const chip = STATUS_CHIPS[status ?? 'PENDING'] ?? STATUS_CHIPS.PENDING;
  return <Chip size='small' color={chip.color} label={chip.label} />;
};
